import React from 'react';
import './docs.css';

/* ── Small helper components ────────────────────────────────────────────── */

function DocsPill({ label, active }: { label: string; active?: boolean }) {
  return (
    <span className={`malpedia-docs-pill${active ? ' active' : ''}`}>
      {label}
    </span>
  );
}

function DocsContextRing({
  pct,
  color,
  label,
}: {
  pct: number;
  color: string;
  label: string;
}) {
  const r = 9;
  const cx = 11;
  const cy = 11;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - pct);

  return (
    <div className="malpedia-docs-ring-item">
      <svg width="22" height="22" viewBox="0 0 22 22">
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth="3"
          className="malpedia-docs-ring-track"
        />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="3"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      </svg>
      <span>{label}</span>
    </div>
  );
}

function DocsPipelineDemo() {
  const steps: { label: string; status: 'done' | 'active' | 'pending' }[] = [
    { label: 'Plan', status: 'done' },
    { label: 'Search', status: 'done' },
    { label: 'Rerank', status: 'active' },
    { label: 'Generate', status: 'pending' },
  ];
  const icon = { done: '✓', active: '⟳', pending: '○' };

  return (
    <div className="malpedia-docs-pipeline-demo">
      {steps.map((s, i) => (
        <React.Fragment key={s.label}>
          <span className={`malpedia-docs-pipeline-step ${s.status}`}>
            <span>{icon[s.status]}</span> {s.label}
          </span>
          {i < steps.length - 1 && (
            <span className="malpedia-docs-pipeline-arrow">→</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return <span className="malpedia-docs-kbd">{children}</span>;
}

/* ── Main page ──────────────────────────────────────────────────────────── */

export default function DocsPage() {
  return (
    <div className="malpedia-docs-page">
      <div className="malpedia-docs-container">
        <h1 className="malpedia-docs-title">Usage Guide</h1>
        <p className="malpedia-docs-subtitle">
          A walkthrough from the chat surface outward — how to ask, how to read
          the answer, and where each clickable element takes you next.
        </p>

        {/* ── 1. Orientation ─────────────────────────────────────────── */}
        <section className="malpedia-docs-section">
          <h2 className="malpedia-docs-section-title">Orientation</h2>

          <p className="malpedia-docs-text">
            Everything starts in <strong>Chat</strong>. Ask a question, read
            the grounded answer, then follow citations, inline IOCs, and
            entity links outward into the rest of the app. The left sidebar
            exposes four primary destinations:
          </p>

          <ul className="malpedia-docs-nav-legend">
            <li>
              <span className="malpedia-docs-nav-icon">💬</span>
              <div>
                <span className="malpedia-docs-feature-label">Chat</span> —
                ask questions against the threat-intel corpus. The default
                landing surface.
              </div>
            </li>
            <li>
              <span className="malpedia-docs-nav-icon">🕸</span>
              <div>
                <span className="malpedia-docs-feature-label">Graph</span> —
                explore actors, families, and techniques as a force-directed
                network.
              </div>
            </li>
            <li>
              <span className="malpedia-docs-nav-icon">🔎</span>
              <div>
                <span className="malpedia-docs-feature-label">Explore</span> —
                full-text and IOC search across the corpus with filters and a
                top-overview dashboard.
              </div>
            </li>
            <li>
              <span className="malpedia-docs-nav-icon">📖</span>
              <div>
                <span className="malpedia-docs-feature-label">Docs</span> —
                this page.
              </div>
            </li>
          </ul>

          <div className="malpedia-docs-note">
            The rest of this guide follows the natural arc of an
            investigation: <strong>chat → response → citation → IOC →
            entity → graph → explore</strong>.
          </div>
        </section>

        {/* ── 2. Chat basics ─────────────────────────────────────────── */}
        <section className="malpedia-docs-section">
          <h2 className="malpedia-docs-section-title">Chat basics</h2>

          <p className="malpedia-docs-text">
            Type your question in the input bar at the bottom. Press{' '}
            <Kbd>Enter</Kbd> to send, or <Kbd>Shift</Kbd>+<Kbd>Enter</Kbd> for
            a new line.
          </p>

          <p className="malpedia-docs-text">
            Use the toggle next to the input to switch between{' '}
            <strong>RAG</strong> and <strong>Chat</strong> mode:
          </p>

          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12 }}>
            <div className="malpedia-docs-toggle-mock">
              <span className="malpedia-docs-toggle-mock-btn active">RAG</span>
              <span className="malpedia-docs-toggle-mock-btn">Chat</span>
            </div>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>or</span>
            <div className="malpedia-docs-toggle-mock">
              <span className="malpedia-docs-toggle-mock-btn">RAG</span>
              <span className="malpedia-docs-toggle-mock-btn active">Chat</span>
            </div>
          </div>

          <p className="malpedia-docs-text">
            <strong>RAG</strong> searches 16,000+ threat-intelligence reports
            to ground its answer in real sources.{' '}
            <strong>Chat</strong> is plain conversation with no document
            retrieval — useful for follow-up reasoning over the answer that
            is already on screen.
          </p>

          <p className="malpedia-docs-text">
            Click the <strong>three-dot button</strong> (⋯) on the input bar
            to open the options popover described next.
          </p>
        </section>

        {/* ── 3. The options popover ─────────────────────────────────── */}
        <section className="malpedia-docs-section">
          <h2 className="malpedia-docs-section-title">The options popover (⋯)</h2>

          <p className="malpedia-docs-text">
            The popover groups every per-message tuning knob: search strategy,
            retrieval depth, enrichment toggles, and (in Advanced) custom
            sliders. Settings persist for the session.
          </p>

          <h3 className="malpedia-docs-subsection-title">Strategy</h3>

          <div className="malpedia-docs-pill-strip">
            <DocsPill label="Auto" active />
            <DocsPill label="Semantic" />
            <DocsPill label="Connected" />
            <DocsPill label="Entity" />
            <DocsPill label="Discovery" />
            <DocsPill label="Planned" />
            <DocsPill label="Chat only" />
          </div>

          <div className="malpedia-docs-table-wrap">
            <table className="malpedia-docs-table">
              <thead>
                <tr>
                  <th>Strategy</th>
                  <th>Description</th>
                  <th>Best for</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Auto</td>
                  <td>Picks the best approach</td>
                  <td>Most questions</td>
                </tr>
                <tr>
                  <td>Semantic</td>
                  <td>Meaning similarity + keywords</td>
                  <td>Conceptual questions</td>
                </tr>
                <tr>
                  <td>Connected</td>
                  <td>Follows entity relationships</td>
                  <td>"What malware does X use?"</td>
                </tr>
                <tr>
                  <td>Entity</td>
                  <td>Looks up actors/families by name</td>
                  <td>Direct entity queries</td>
                </tr>
                <tr>
                  <td>Discovery</td>
                  <td>Explores unexpected connections</td>
                  <td>Open-ended research</td>
                </tr>
                <tr>
                  <td>Planned</td>
                  <td>AI planner combines strategies</td>
                  <td>Complex multi-part questions</td>
                </tr>
                <tr>
                  <td>Chat only</td>
                  <td>No document search</td>
                  <td>Follow-up clarifications</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="malpedia-docs-note">
            <strong>Simple</strong> shows four strategies (Auto, Semantic,
            Entity, Chat only). Toggle{' '}
            <span className="malpedia-docs-badge-advanced">Advanced</span>{' '}
            inside the popover to reveal all seven.
          </div>

          <h3 className="malpedia-docs-subsection-title">Search depth</h3>

          <div className="malpedia-docs-pill-strip">
            <DocsPill label="Auto" />
            <DocsPill label="Quick" />
            <DocsPill label="Balanced" active />
            <DocsPill label="Thorough" />
            <DocsPill label="Deep" />
            <DocsPill label="Custom" />
          </div>

          <div className="malpedia-docs-table-wrap">
            <table className="malpedia-docs-table">
              <thead>
                <tr>
                  <th>Depth</th>
                  <th>~Passages</th>
                  <th>Best for</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Auto</td>
                  <td>System decides</td>
                  <td>Let the system adapt</td>
                </tr>
                <tr>
                  <td>Quick</td>
                  <td>~4</td>
                  <td>Fast lookups, specific entities</td>
                </tr>
                <tr>
                  <td>Balanced</td>
                  <td>~8</td>
                  <td>Most questions</td>
                </tr>
                <tr>
                  <td>Thorough</td>
                  <td>~20</td>
                  <td>Complex topics, comparisons</td>
                </tr>
                <tr>
                  <td>Deep</td>
                  <td>~50</td>
                  <td>Research, campaign analysis</td>
                </tr>
                <tr>
                  <td>Custom</td>
                  <td>You choose</td>
                  <td>Fine-tune all four sliders</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="malpedia-docs-subsection-title">Enrichment toggles</h3>

          <ul className="malpedia-docs-toggle-list">
            <li>
              <span className="malpedia-docs-toggle-name">IOC search</span> —
              detects IPs, hashes, and domains in your query and pivots to
              related threats.
            </li>
            <li>
              <span className="malpedia-docs-toggle-name">Web search</span> —
              supplements the local corpus with live internet results. When
              active, the retrieval card shows a 🌐 indicator.
            </li>
            <li>
              <span className="malpedia-docs-toggle-name">Expand context</span>
              <span className="malpedia-docs-badge-advanced">Advanced</span> —
              retrieves neighboring chunks around each match for broader
              context.
            </li>
            <li>
              <span className="malpedia-docs-toggle-name">Full document</span>
              <span className="malpedia-docs-badge-advanced">Advanced</span> —
              retrieves entire documents instead of individual chunks.
            </li>
          </ul>

          <div className="malpedia-docs-note">
            <strong>Expand context</strong> and <strong>Full document</strong>{' '}
            are mutually exclusive — enabling one disables the other.
          </div>

          <h3 className="malpedia-docs-subsection-title">
            Custom sliders
            <span className="malpedia-docs-badge-advanced">Advanced</span>
          </h3>

          <p className="malpedia-docs-text">
            Selecting <strong>Custom</strong> depth in Advanced mode reveals
            four steppers:
          </p>

          <div className="malpedia-docs-table-wrap">
            <table className="malpedia-docs-table">
              <thead>
                <tr>
                  <th>Slider</th>
                  <th>Range</th>
                  <th>Controls</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Candidates</td>
                  <td>1 – 200</td>
                  <td>Initial passage pool fetched before reranking</td>
                </tr>
                <tr>
                  <td>Context size</td>
                  <td>1 – 100</td>
                  <td>Passages handed to the LLM after reranking</td>
                </tr>
                <tr>
                  <td>Merge limit</td>
                  <td>5 – 500</td>
                  <td>Cap on candidates merged across hybrid sources</td>
                </tr>
                <tr>
                  <td>Per report</td>
                  <td>1 – 10</td>
                  <td>Max passages drawn from any single document</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="malpedia-docs-subsection-title">
            Context ring &amp; token counter
          </h3>

          <p className="malpedia-docs-text">
            The small ring next to the input bar shows how much of the
            context window the current conversation occupies. Color shifts as
            usage increases:
          </p>

          <div className="malpedia-docs-ring-demo">
            <DocsContextRing pct={0.25} color="var(--color-green)" label="< 50 % — green" />
            <DocsContextRing pct={0.65} color="var(--color-yellow)" label="50–75 % — yellow" />
            <DocsContextRing pct={0.85} color="var(--color-orange)" label="75–90 % — orange" />
            <DocsContextRing pct={0.95} color="var(--color-red)" label="> 90 % — red" />
          </div>

          <p className="malpedia-docs-text">
            A live token counter sits beside the ring; once a single message
            estimate exceeds <strong>~8,000 tokens</strong> it switches to a
            warning state to flag that you may want to trim context or start
            a new session.
          </p>

          <div className="malpedia-docs-note">
            The popover footer has a <strong>"Full settings →"</strong> link
            that opens <code>/settings</code>, where defaults for every
            option above can be set permanently.
          </div>
        </section>

        {/* ── 4. Reading a response ──────────────────────────────────── */}
        <section className="malpedia-docs-section">
          <h2 className="malpedia-docs-section-title">Reading a response</h2>

          <p className="malpedia-docs-text">
            An assistant message is more than its body text — several
            affordances are layered around it.
          </p>

          <h3 className="malpedia-docs-subsection-title">Pipeline progress</h3>

          <p className="malpedia-docs-text">
            While the system works, a step indicator above the answer shows
            where it is. Each step transitions from pending (○) to active
            (⟳) to done (✓):
          </p>

          <DocsPipelineDemo />

          <h3 className="malpedia-docs-subsection-title">Retrieval card</h3>

          <p className="malpedia-docs-text">
            Every RAG answer gets a retrieval card. Collapsed, it shows the
            mode used, a confidence percentage, and a 🌐 indicator if web
            search was part of the answer. Expand it to see:
          </p>

          <ul className="malpedia-docs-feature-list">
            <li>
              <span className="malpedia-docs-feature-label">Planning</span> —
              the planner's chosen strategy mix and any sub-queries it
              issued.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Retrieve</span> —
              candidate counts at each stage (initial pool → merged →
              reranked) and per-source breakdowns.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Profile</span> —
              the depth profile and any custom slider values that were in
              effect.
            </li>
          </ul>

          <p className="malpedia-docs-text">
            The card header has two utilities: an <strong>ⓘ info toast</strong>{' '}
            with a quick legend, and a <strong>⬇ markdown export</strong>{' '}
            button that copies the full retrieval trace as markdown.
          </p>

          <h3 className="malpedia-docs-subsection-title">In-line affordances</h3>

          <ul className="malpedia-docs-feature-list">
            <li>
              <span className="malpedia-docs-feature-label">Citations [1]</span>{' '}
              — numbered superscripts in the answer text. Hover for ~300 ms
              to see a preview popover; click to open the full Source View.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">IOC highlights</span>{' '}
              — IPs, hashes, and domains are marked inline. Click to open
              the IOC detail page; right-click for a context menu (copy,
              external lookups).
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Entity links</span>{' '}
              — extracted families, actors, and techniques link directly
              into the Graph Explorer with that node selected.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">💡 Reasoning</span>{' '}
              — when the model emits a <code>&lt;think&gt;</code> block, a
              collapsible "Reasoning" section appears above the answer.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">📑 N sources</span>{' '}
              — a collapsible footer below the answer lists every cited
              document as a chip; clicking a chip jumps to the Source View.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">⎘ Copy</span> —
              every assistant message has a copy button in its toolbar.
            </li>
          </ul>
        </section>

        {/* ── 5. Sidebar & sessions ──────────────────────────────────── */}
        <section className="malpedia-docs-section">
          <h2 className="malpedia-docs-section-title">Sidebar &amp; sessions</h2>

          <p className="malpedia-docs-text">
            The left sidebar tracks chat history and gives you a few
            session-management actions at the top:{' '}
            <strong>New Chat</strong>, <strong>Temporary Chat</strong>{' '}
            (no history persisted), and <strong>Export</strong> (download the
            current thread as JSON or markdown).
          </p>

          <p className="malpedia-docs-text">
            Past sessions live in a foldered tree below — drag to reorder,
            right-click any session for{' '}
            <strong>rename</strong> / <strong>delete</strong>, and use the{' '}
            <strong>Clear All</strong> footer button to nuke history in one
            shot (with a confirm prompt).
          </p>
        </section>

        {/* ── 6. Following a citation: Source View ───────────────────── */}
        <section className="malpedia-docs-section">
          <h2 className="malpedia-docs-section-title">
            Following a citation: the Source View
          </h2>

          <p className="malpedia-docs-text">
            Clicking a <code>[1]</code> citation or a chip in the
            "📑 N sources" footer opens the Source View for that document.
            Top to bottom, the page lays out:
          </p>

          <ul className="malpedia-docs-feature-list">
            <li>
              <span className="malpedia-docs-feature-label">Header</span> —
              the cite key, publication date, document title, and a link to
              the original blog/report.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Malpedia attribution bar</span>{' '}
              — families, actors, and techniques Malpedia has linked to this
              document, each clickable to its entity page.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">AI summary</span>{' '}
              — a concise LLM-generated summary with extracted entities
              tagged inline.
            </li>
          </ul>
        </section>

        {/* ── 7. Pivoting on an IOC ──────────────────────────────────── */}
        <section className="malpedia-docs-section">
          <h2 className="malpedia-docs-section-title">Pivoting on an IOC</h2>

          <p className="malpedia-docs-text">
            Clicking any inline IOC opens its detail page:
          </p>

          <ul className="malpedia-docs-feature-list">
            <li>
              <span className="malpedia-docs-feature-label">Type badge</span>{' '}
              — IP, domain, SHA-256, MD5, CVE, etc.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Copy &amp; external lookups</span>{' '}
              — one-click copy plus deep links to{' '}
              <strong>VirusTotal</strong>, <strong>NVD</strong>, and{' '}
              <strong>MITRE</strong> where relevant.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">CVE metadata</span>{' '}
              — for CVEs: CVSS score, vector, affected products, and
              published date.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Related families &amp; actors</span>{' '}
              — every entity that appears alongside this IOC in the corpus.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Document references</span>{' '}
              — a sortable table of every report that mentions this IOC.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Co-occurring IOCs</span>{' '}
              — other indicators that appear in the same documents, grouped
              by type.
            </li>
          </ul>
        </section>

        {/* ── 8. Entity detail pages ─────────────────────────────────── */}
        <section className="malpedia-docs-section">
          <h2 className="malpedia-docs-section-title">Entity detail pages</h2>

          <p className="malpedia-docs-text">
            Actors, families, techniques, and countries all share the same
            page shell — only the data differs. Each page contains:
          </p>

          <ul className="malpedia-docs-feature-list">
            <li>
              <span className="malpedia-docs-feature-label">Header &amp; action links</span>{' '}
              — entity name with deep links to{' '}
              <strong>Malpedia</strong>, <strong>MITRE</strong>, and an{' '}
              <strong>"Open in Graph"</strong> button that drops you into
              Graph Explorer with this node centered.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Facts band</span>{' '}
              — high-level stats (first seen, last seen, document count) and
              a sparkline showing report frequency over time.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Referenced documents</span>{' '}
              — paginated list of every report mentioning this entity.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Co-attributed entities</span>{' '}
              — actors/families/techniques that frequently appear together
              with this one.
            </li>
          </ul>
        </section>

        {/* ── 9. Graph Explorer ──────────────────────────────────────── */}
        <section className="malpedia-docs-section">
          <h2 className="malpedia-docs-section-title">
            Graph Explorer (<code>/graph</code>)
          </h2>

          <p className="malpedia-docs-text">
            Reach the graph from any entity link, the sidebar, or by typing
            a seed into its search bar. The view is force-directed with
            color-coded nodes per type (actor, family, technique, country,
            IOC).
          </p>

          <ul className="malpedia-docs-feature-list">
            <li>
              <span className="malpedia-docs-feature-label">Legend toggles</span>{' '}
              — show/hide each node type to declutter dense graphs.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Click a node</span>{' '}
              — opens a side panel with the entity's facts and links.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Double-click</span>{' '}
              — expands neighbors (capped at 20 per expansion to keep the
              layout stable).
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Drag &amp; pin</span>{' '}
              — drag a node to reposition; release to pin it in place so the
              layout converges around it.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Smart View</span>{' '}
              — when a node is selected, Smart View collapses unrelated
              nodes and surfaces the most relevant neighbors.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Save graphs</span>{' '}
              — save the current layout to the sidebar list to return to it
              later.
            </li>
          </ul>
        </section>

        {/* ── 10. Explore ────────────────────────────────────────────── */}
        <section className="malpedia-docs-section">
          <h2 className="malpedia-docs-section-title">
            Explore (<code>/explore</code>)
          </h2>

          <p className="malpedia-docs-text">
            Explore is the bulk-search counterpart to Chat — useful when you
            already know roughly what you're looking for and want
            paginated results rather than a synthesized answer.
          </p>

          <ul className="malpedia-docs-feature-list">
            <li>
              <span className="malpedia-docs-feature-label">Search bar</span>{' '}
              — auto-detects IOCs in the query (IP, hash, domain, CVE) and
              switches to indicator search transparently.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Filters</span> —
              category, IOC type, and sort (relevance, date, doc count).
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Top dashboard</span>{' '}
              — before any query, the page shows overview cards (top
              actors, top families, recent reports) so you have somewhere
              to start.
            </li>
            <li>
              <span className="malpedia-docs-feature-label">Result rows</span>{' '}
              — paginated, each showing document count and relevance score.
            </li>
          </ul>
        </section>

        {/* ── 11. Keyboard shortcuts ─────────────────────────────────── */}
        <section className="malpedia-docs-section">
          <h2 className="malpedia-docs-section-title">Keyboard shortcuts</h2>

          <table className="malpedia-docs-shortcut-table">
            <tbody>
              <tr>
                <td><Kbd>Enter</Kbd></td>
                <td>Send message</td>
              </tr>
              <tr>
                <td><Kbd>Shift</Kbd>+<Kbd>Enter</Kbd></td>
                <td>New line</td>
              </tr>
            </tbody>
          </table>

          <p className="malpedia-docs-text" style={{ marginTop: 12 }}>
            These are the only globally-bound shortcuts. Everything else is
            click-driven.
          </p>
        </section>
      </div>
    </div>
  );
}
